import { useState, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from '@xstate/store/react';
import {
  Eye, EyeOff, Lock, Unlock, Trash2, ChevronUp, ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { useCanvasStores, useCanvasLayers } from '../../stores/CanvasContext';

export const LayerHeaderRow = memo(function LayerHeaderRow({ layerId }: { layerId: string }) {
  const { t } = useTranslation();
  const { store, uiStore } = useCanvasStores();
  const layers = useCanvasLayers();

  const layer = layers.find(l => l.id === layerId);
  const arrIdx = layers.findIndex(l => l.id === layerId);
  const isActive = useSelector(store, s => s.context.activeLayerId === layerId);
  const isCollapsed = useSelector(uiStore, s => s.context.layerPanelCollapsedLayers.has(layerId));

  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');

  const handleStartRename = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (layer) setEditName(layer.name);
    setIsEditing(true);
  }, [layer]);

  const handleRenameSubmit = useCallback(() => {
    const name = editName.trim();
    if (name) {
      store.send({ type: 'layer.update', layerId, changes: { name } });
    }
    setIsEditing(false);
    setEditName('');
  }, [store, layerId, editName]);

  const handleRenameCancel = useCallback(() => {
    setIsEditing(false);
    setEditName('');
  }, []);

  if (!layer) return null;

  return (
    <div
      className={`flex items-center gap-1 px-2 py-1.5 cursor-pointer ${
        isActive ? 'bg-blue-50' : 'hover:bg-gray-50'
      }`}
      onClick={() => { store.send({ type: 'layer.setActive', layerId }); }}
    >
      {/* Expand/collapse toggle */}
      <button
        className="p-0 text-gray-400 hover:text-gray-600 shrink-0"
        onClick={(e) => { e.stopPropagation(); uiStore.send({ type: 'layerPanel.toggleLayerCollapsed', layerId }); }}
      >
        {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
      </button>

      {/* Visibility */}
      <button
        onClick={(e) => { e.stopPropagation(); store.send({ type: 'layer.update', layerId, changes: { visible: !layer.visible } }); }}
        className="p-0.5 text-gray-400 hover:text-gray-600 shrink-0"
        title={layer.visible ? t('canvasLayer.hide') : t('canvasLayer.show')}
      >
        {layer.visible ? <Eye size={11} /> : <EyeOff size={11} className="text-gray-300" />}
      </button>

      {/* Lock */}
      <button
        onClick={(e) => { e.stopPropagation(); store.send({ type: 'layer.update', layerId, changes: { locked: !layer.locked } }); }}
        className="p-0.5 text-gray-400 hover:text-gray-600 shrink-0"
        title={layer.locked ? t('canvasLayer.unlock') : t('canvasLayer.lock')}
      >
        {layer.locked ? <Lock size={10} className="text-orange-400" /> : <Unlock size={10} className="text-gray-300" />}
      </button>

      {/* Name */}
      {isEditing ? (
        <input
          autoFocus
          value={editName}
          onChange={e => setEditName(e.target.value)}
          onBlur={handleRenameSubmit}
          onKeyDown={e => {
            if (e.key === 'Enter') handleRenameSubmit();
            if (e.key === 'Escape') handleRenameCancel();
          }}
          className="flex-1 text-xs px-1 py-0.5 border border-blue-300 rounded bg-white text-gray-900 focus:outline-none min-w-0"
          onClick={e => e.stopPropagation()}
        />
      ) : (
        <span
          className={`flex-1 text-xs font-medium truncate ${layer.visible ? 'text-gray-700' : 'text-gray-400'}`}
          onDoubleClick={handleStartRename}
        >
          {layer.name}
        </span>
      )}

      {/* Move up/down (panel is reversed: top = highest array index) */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (arrIdx < 0 || arrIdx >= layers.length - 1) return;
          const newOrder = layers.map(l => l.id);
          [newOrder[arrIdx], newOrder[arrIdx + 1]] = [newOrder[arrIdx + 1], newOrder[arrIdx]];
          store.send({ type: 'layer.reorder', layerIds: newOrder });
        }}
        className="p-0.5 text-gray-300 hover:text-gray-500 disabled:opacity-30 shrink-0"
        disabled={arrIdx >= layers.length - 1}
        title={t('canvasLayer.moveUp')}
      >
        <ChevronUp size={10} />
      </button>
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (arrIdx <= 0) return;
          const newOrder = layers.map(l => l.id);
          [newOrder[arrIdx], newOrder[arrIdx - 1]] = [newOrder[arrIdx - 1], newOrder[arrIdx]];
          store.send({ type: 'layer.reorder', layerIds: newOrder });
        }}
        className="p-0.5 text-gray-300 hover:text-gray-500 disabled:opacity-30 shrink-0"
        disabled={arrIdx <= 0}
        title={t('canvasLayer.moveDown')}
      >
        <ChevronDown size={10} />
      </button>

      {/* Delete */}
      {layers.length > 1 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            store.send({ type: 'layer.remove', layerId });
            if (isActive) {
              const remaining = layers.filter(l => l.id !== layerId);
              if (remaining.length > 0) {
                store.send({ type: 'layer.setActive', layerId: remaining[0].id });
              }
            }
          }}
          className="p-0.5 text-gray-300 hover:text-red-400 shrink-0"
          title={t('canvasLayer.removeLayer')}
        >
          <Trash2 size={10} />
        </button>
      )}
    </div>
  );
});
